import { t } from '~i18n';
import styles from './DeviceMapOverlay.module.css';
import { Button } from '@ixicare/ui';
import { Beacon, Device, DeviceType, ECoordsSrid, Gateway, TUpdateCoords } from '@ixicare/resources';
import { useMutation } from '@tanstack/react-query';
import { DBG } from '~modules/devices/components/DeviceHealthTable/types';
import { OnMarkerInsertEvent } from '~components/Map/DynamicMap';
import { Spacer } from '~components/Utils/Spacer';
import { useStores } from '~/store/utils';
import { useDeviceArchive } from '~modules/sites/hooks/useDeviceArchive';
import { DeleteDeviceModal } from '~modules/devices/components/DeviceModals/DeleteDeviceModal';

const DeviceMapOverlay = ({
  device,
  type,
  onClose,
  onRefresh,
  onMoveStart,
  onMoveEnd,
  isMoving,
  newLocation,
  isEditing,
  onEditStart,
}) => {
  const { notifications } = useStores();

  const { devicesToArchive, setDevicesToArchive, deleteMutation } = useDeviceArchive({
    onClose,
    onRefresh,
    notifications,
  });

  const moveMutation = useMutation({
    mutationFn: () => updateDevice(type, device, newLocation),
    onSuccess: () => {
      onClose();
      onMoveEnd();
      onRefresh();
    },
  });

  return (
    <div className={styles.DeviceMapOverlay}>
      <button className={styles.Close} onClick={onClose}>
        <i className="icon-cross" />
      </button>
      <dl>
        <dt>{t('devices.table.deviceType')}</dt>
        {type === 'WALL_MOUNTED_DISTRESS_BUTTON' ? (
          <dd>{t(`device.types.${device.subType ? `${device.subType.toLowerCase()}` : `regular_button`}`)}</dd>
        ) : (
          <dd>{t(`device.types.${type.toLowerCase()}`)}</dd>
        )}
      </dl>
      <dl>
        <dt>{t('devices.table.macAddress')}</dt>
        <dd>{device.macAddress}</dd>
      </dl>

      <dl>
        <dt>{t('devices.table.description')}</dt>
        <dd>{device.description ?? '-'}</dd>
      </dl>
      <Spacer size={20} />

      {isMoving ? (
        <>
          <Button
            isBlock
            buttonType="success"
            loading={moveMutation.isPending}
            disabled={!newLocation}
            onClick={() => moveMutation.mutate()}
          >
            {newLocation?.zoneUuid
              ? t('beamSettings.movingIndicator.moveToRoom', {
                  room: newLocation.zoneName,
                })
              : t('beamSettings.movingIndicator.moveOutsideOfRoom')}
          </Button>
          <Spacer size={5} />
          <Button isBlock buttonType="error" onClick={onMoveEnd}>
            {t('global.cancel')}
          </Button>
        </>
      ) : (
        <>
          <Button isBlock buttonType="success" disabled={isEditing} onClick={onEditStart}>
            {t('site.map.deviceMapOverlay.edit')}
          </Button>
          <Spacer size={5} />
          <Button isBlock buttonType="primary" disabled={isMoving} onClick={onMoveStart}>
            {t('site.map.deviceMapOverlay.move')}
          </Button>
          <Spacer size={5} />
          <Button
            isBlock
            loading={deleteMutation.isPending}
            buttonType="error"
            onClick={() => setDevicesToArchive([{ ...device, type }])}
          >
            {t('site.map.deviceMapOverlay.delete')}
          </Button>
        </>
      )}
      {devicesToArchive.length > 0 && (
        <DeleteDeviceModal
          devices={devicesToArchive}
          onClose={() => setDevicesToArchive([])}
          onDelete={() => deleteMutation.mutate()}
          isPending={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

const updateDevice = async (type: DeviceType, device: DBG, newLocation: OnMarkerInsertEvent) => {
  const installationCoords: TUpdateCoords | undefined = newLocation && {
    x: newLocation.x,
    y: newLocation.y,
    srid: ECoordsSrid.Indoor,
  };

  if (type === DeviceType.BEACON) {
    await Beacon.patchBeacon(device.id, {
      zoneId: newLocation?.zoneUuid,
      planeId: newLocation?.planeId,
      installationCoords,
      distanceToLeftWall: 0,
      distanceToRightWall: 0,
    });
  }
  if (type === DeviceType.GATEWAY) {
    await Gateway.patchGateway(device.id, {
      zoneId: newLocation?.zoneUuid,
      planeId: newLocation?.planeId,
      installationCoords,
    });
  }
  if (type === DeviceType.WALL_MOUNTED_DISTRESS_BUTTON) {
    await Device.patchDevice(device.id, {
      installationCoords,
      installationZoneId: newLocation?.zoneUuid,
      planeId: newLocation?.planeId,
    });
  }
};

export default DeviceMapOverlay;
